import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Zap, Globe, Lock, Rocket } from "lucide-react";
import { getAppBySlug, getStores } from "@/apps";
import Footer from "@/Footer";
import NavBar from "@/NavBar";
import NotFound from "@/NotFound";
import { BetaSignupDialog } from "@/Forms";
import PhoneMockup from "@/PhoneMockup";

const AppleIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 384 512" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const PlayIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 512 512" width={size} height={size} aria-hidden="true">
    <path fill="#00d0ff" d="M47 12C41 15 37 21 37 30v452c0 9 4 15 10 18l236-244z"/>
    <path fill="#00e676" d="M47 12c5-3 12-2 19 2l268 154-51 62z"/>
    <path fill="#ffd600" d="M334 168l64 37c20 12 20 32 0 43l-64 37-51-77z"/>
    <path fill="#ff3d00" d="M47 500c5 3 12 2 19-2l268-154-51-62z"/>
  </svg>
);

const StoreButton = ({ href, disabled, top, bottom, icon, testid }) => {
  const cls =
    "hi-cta inline-flex items-center gap-3 rounded-xl px-5 py-2.5 border " +
    (disabled
      ? "border-white/10 bg-white/5 text-white/40 cursor-default"
      : "border-white/15 bg-black/40 text-white hover:bg-black/70 hover:border-[#3b9bff]/50");
  const content = (
    <>
      <span className="shrink-0">{icon}</span>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-wider text-white/50">{top}</span>
        <span className="block text-sm font-semibold hi-body">{bottom}</span>
      </span>
    </>
  );
  if (disabled) return <div data-testid={testid} className={cls}>{content}</div>;
  return (
    <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {content}
    </a>
  );
};

const AppDetail = () => {
  const { slug } = useParams();
  const app = getAppBySlug(slug);
  if (!app) return <NotFound />;

  const cleanName = app.name.replace(/\u2122/g, "").trim();
  const { appStore, playStore, playTesting } = getStores(app);
  const uploaded = Array.isArray(app.screenshots) ? app.screenshots : [];
  const framed = app.framedScreenshots;
  const phoneShots = app.url
    ? [
        `https://image.thum.io/get/viewportWidth/390/width/390/crop/844/noanimate/${app.url}`,
        `https://image.thum.io/get/viewportWidth/390/width/390/crop/1500/noanimate/${app.url}`,
        `https://image.thum.io/get/viewportWidth/390/width/390/crop/2200/noanimate/${app.url}`,
      ]
    : [];

  return (
    <div className="hi-bg hi-grain min-h-screen">
      <NavBar />
      <div className="relative z-[2] max-w-6xl mx-auto px-5 py-8 md:py-12">
        {/* Back */}
        <Link
          to="/"
          data-testid="back-to-suite"
          className="hi-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
        >
          <ArrowLeft size={16} strokeWidth={2.5} /> Back to Suite
        </Link>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 grid md:grid-cols-[auto,1fr] gap-7 items-center"
        >
          <img
            src={app.logo}
            alt={`${app.name} logo`}
            className="w-28 h-28 md:w-36 md:h-36 rounded-3xl object-cover hi-logo-glow"
          />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="detail-title" className="hi-serif text-3xl md:text-5xl text-[#f3ead4]">
                {app.name}
              </h1>
              {app.beta && (
                <span
                  data-testid="beta-badge"
                  className="hi-body text-[11px] font-bold uppercase tracking-wider text-[#7fe0a0] bg-[#1f7a45]/25 border border-[#39c274]/40 rounded-full px-3 py-1 inline-flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" /> Beta
                </span>
              )}
              {!app.live && (
                <span className="hi-body text-[11px] font-bold uppercase tracking-wider text-[#3b9bff] bg-[#3b9bff]/12 border border-[#3b9bff]/30 rounded-full px-3 py-1">
                  Coming Soon
                </span>
              )}
            </div>
            <p className="hi-body text-sm uppercase tracking-[0.18em] mt-2" style={{ color: app.accent }}>
              {app.tagline}
            </p>
            <p className="hi-body text-base text-white/65 leading-relaxed mt-4 max-w-2xl">
              {app.longDescription || app.description}
            </p>

            {/* CTA row */}
            <div className="flex flex-wrap items-center gap-3 mt-6">
              {app.url ? (
                <a
                  data-testid="open-web-app"
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hi-cta inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-[#c9a24b] text-[#1a2417] hover:bg-[#e0bb63]"
                >
                  <Globe size={17} strokeWidth={2.5} /> Open Web App <ArrowUpRight size={16} strokeWidth={2.5} />
                </a>
              ) : (
                <div className="hi-cta inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold hi-body bg-white/5 border border-white/10 text-white/45 cursor-default">
                  <Lock size={16} strokeWidth={2.5} /> Web App Coming Soon
                </div>
              )}
              <StoreButton
                testid="btn-appstore"
                href={appStore}
                disabled={!app.live}
                icon={<AppleIcon />}
                top={app.live ? "Download on the" : "Coming to the"}
                bottom="App Store"
              />
              <StoreButton
                testid="btn-playstore"
                href={playStore}
                disabled={!app.live}
                icon={<PlayIcon />}
                top={app.live ? "Get it on" : "Coming to"}
                bottom="Google Play"
              />
              {app.beta && (
                <BetaSignupDialog
                  defaultApps={[cleanName]}
                  trigger={
                    <button
                      data-testid="detail-join-beta"
                      className="hi-cta inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-bold hi-body bg-gradient-to-r from-[#1f5fbf] to-[#3b9bff] text-white hover:brightness-110"
                    >
                      <Rocket size={16} strokeWidth={2.5} /> Join the Beta
                    </button>
                  }
                />
              )}
            </div>

            {app.beta && (
              <p
                data-testid="beta-note"
                className="hi-body text-[13px] text-[#7fe0a0] mt-4 inline-flex items-center gap-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />
                This app is currently in <strong className="text-[#a7f3c4]">beta testing</strong> — features are live and improving. Your feedback is welcome.
              </p>
            )}
            {playTesting && (
              <a
                href={playTesting}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="play-testing-link"
                className="hi-body inline-flex items-center gap-2 text-[13px] text-[#8fbaff] hover:text-white mt-2"
              >
                <ArrowUpRight size={14} strokeWidth={2.5} /> Join the Android beta on Google Play
              </a>
            )}
          </div>
        </motion.section>

        {/* Feature deep-dive */}
        {Array.isArray(app.features) && app.features.length > 0 && (
          <section className="mt-14">
            <h2 className="hi-serif text-2xl text-[#f3ead4] mb-6">Features</h2>
            <div data-testid="detail-features" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {app.features.map((f, i) => (
                <motion.div
                  key={f.name}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="hi-card p-6"
                >
                  <span
                    className={`grid place-items-center w-10 h-10 rounded-xl mb-4 ${
                      f.pro ? "bg-[#c9a24b]/20 text-[#e0bb63]" : "bg-white/8 text-white/60"
                    }`}
                  >
                    {f.pro ? <Zap size={18} strokeWidth={2.6} /> : <Check size={18} strokeWidth={3} />}
                  </span>
                  <div className="flex items-center gap-2">
                    <h3 className="hi-body font-semibold text-[#f3ead4]">{f.name}</h3>
                    {f.pro && (
                      <span className="text-[9px] font-bold tracking-wider uppercase text-[#1a2417] bg-[#c9a24b] rounded px-1.5 py-[1px]">
                        Pro
                      </span>
                    )}
                  </div>
                  <p className="hi-body text-sm text-white/70 leading-relaxed mt-2">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Screenshots — phone mockups */}
        <section className="mt-14">
          <h2 className="hi-serif text-2xl text-[#f3ead4] mb-2">Screenshots</h2>
          <p className="hi-body text-sm text-white/65 mb-8">
            {app.live ? "A look at the app on your phone." : "A preview of what's coming."}
          </p>
          {uploaded.length > 0 ? (
            <div data-testid="detail-gallery">
              {/* Featured hero */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex justify-center mb-12"
              >
                {framed ? (
                  <img
                    src={uploaded[0]}
                    alt={`${cleanName} featured screenshot`}
                    data-testid="shot-1"
                    className="w-full max-w-[320px] rounded-[32px] drop-shadow-[0_35px_60px_rgba(0,0,0,0.7)] ring-1 ring-white/10"
                  />
                ) : (
                  <PhoneMockup src={uploaded[0]} alt={`${cleanName} featured screenshot`} testid="shot-1" large />
                )}
              </motion.div>

              {/* Remaining */}
              {uploaded.length > 1 && (
                <div className={`grid gap-6 justify-items-center ${framed ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"}`}>
                  {uploaded.slice(1).map((src, i) =>
                    framed ? (
                      <motion.img
                        key={i}
                        src={src}
                        alt={`${cleanName} screenshot ${i + 2}`}
                        loading="lazy"
                        data-testid={`shot-${i + 2}`}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                        className="w-full max-w-[200px] rounded-[24px] drop-shadow-[0_25px_45px_rgba(0,0,0,0.6)]"
                      />
                    ) : (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                      >
                        <PhoneMockup src={src} alt={`${cleanName} screenshot ${i + 2}`} testid={`shot-${i + 2}`} />
                      </motion.div>
                    )
                  )}
                </div>
              )}
            </div>
          ) : phoneShots.length > 0 ? (
            <div data-testid="detail-gallery" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
              {phoneShots.slice(0, 3).map((src, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <PhoneMockup src={src} alt={`${cleanName} screenshot ${i + 1}`} testid={`shot-${i + 1}`} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div data-testid="detail-gallery" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
              {[0, 1, 2].map((i) => (
                <div key={i} data-testid={`shot-${i + 1}`} className="hi-phone mx-auto">
                  <div className="hi-phone-notch" />
                  <div className="hi-phone-screen grid place-items-center bg-[#0c1626]">
                    <div className="text-center px-4">
                      <img src={app.logo} alt={app.name} className="w-16 h-16 rounded-2xl object-cover mx-auto opacity-90" />
                      <p className="hi-body text-white/60 text-xs mt-3">Preview coming soon</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Bottom back */}
        <div className="mt-14 text-center">
          <Link
            to="/"
            data-testid="back-to-suite-bottom"
            className="hi-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold hi-body bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back to Suite
          </Link>
        </div>
      </div>
      <div className="relative z-[2]">
        <Footer />
      </div>
    </div>
  );
};

export default AppDetail;
